/**
 * learningGraph.js — MOAT 1.0 Phase 1 · ONE canonical Learning Graph facade.
 * CHILD → AGE → YEAR → CURRICULUM → SUBJECT → (UNIT) → OBJECTIVE → SKILL → LESSON/ACTIVITY → PRACTICE → EVIDENCE → MASTERY → REVIEW → NEXT.
 * Built from EXISTING sources only: syllabus (objectives), skillGraph (skills), gameCatalog (activities), units (backend), mastery/skillLedger (evidence).
 * No new content store, no new progress store.
 */
import { SYLLABUS } from "@/lib/syllabus";
import { matchesCurriculum } from "@/lib/canonical";
import { outcomesForSubject } from "@/lib/subjectAliases";
import { SKILLS } from "@/lib/skillGraph";
import { GAMES } from "@/lib/gameCatalog";
import { loadMastery } from "@/lib/mastery";
import { getAllSkills } from "@/lib/skillLedger";
import { reviewDue } from "@/lib/reuseEngine";
import { outcomeState } from "@/lib/focusDay";
import { SUBJECTS, ageForYear, bandForYear } from "@/lib/canonical";

export const NODE_TYPES = ["child", "age", "year", "curriculum", "subject", "unit", "objective", "skill", "knowledge", "lesson", "activity", "practice", "question", "response", "evidence", "mastery", "review", "next"];

/** Objectives (curriculum outcomes) for subject × year × optional curriculum, each with its live evidence state. */
export function objectivesFor(subject, year, curriculum = "") {
  const all = outcomesForSubject(SYLLABUS, subject, ageForYear(year));
  const mastery = loadMastery(); const due = reviewDue();
  return (curriculum ? all.filter((o) => matchesCurriculum(o.curriculum, curriculum)) : all).map((o) => ({ type: "objective", id: o.code, label: o.outcome, subject, year, curriculum: o.curriculum, state: outcomeState(o.code, mastery, due), skills: skillsForObjective(o.code) }));
}

/** Skills whose framework mapping points at this objective code (skillGraph is the canonical skill layer). */
export function skillsForObjective(code) {
  return SKILLS.filter((s) => Object.values(s.frameworks || {}).includes(code)).map((s) => ({ type: "skill", id: s.id, label: s.label, subject: s.subject, difficulty: s.difficulty, prerequisites: s.prerequisites || [] }));
}

/** Activities (games) that practise a subject — activity layer; lessons/units come from the backend registry. */
export function activitiesFor(subject) {
  return GAMES.filter((g) => (g.subjects || []).map((s) => String(s).toLowerCase()).includes(subject) || g.category === subject)
    .map((g) => ({ type: "activity", id: g.id, label: g.label, to: g.to, subject, minutes: g.minutes || 5, twoPlayer: !!g.twoPlayer }));
}

/** Lesson / practice / check routes for an objective — existing routes only. */
export const routesFor = (objective, ready = null) => ({
  learn: `/learn-topics?s=${objective.subject}&y=${objective.year}`,
  practice: ready && ready.has(objective.id) ? `/practice/${objective.id}` : `/learn-topics/${objective.id}/test`,
  check: `/learn-topics/${objective.id}/test`,
});

/** Evidence → mastery → review summary for a subject scope. Deterministic; AI never writes here. */
export function subjectSummary(subject, year, curriculum = "") {
  const objs = objectivesFor(subject, year, curriculum);
  const counts = { mastered: 0, confident: 0, practising: 0, review: 0, not_assessed: 0 };
  objs.forEach((o) => { counts[o.state] += 1; });
  const ledger = Object.entries(getAllSkills() || {}).filter(([k]) => k.startsWith(`${subject}:`) || k.includes(`:${subject}-`));
  return { subject, year, band: bandForYear(year), objectives: objs.length, ...counts, ledgerSkills: ledger.length, coverage: objs.length === 0 ? "not_available" : objs.length < 4 ? "partial" : "verified" };
}

/** Whole-graph slice for a child: every canonical subject at the child's year. */
export function graphForChild({ year, curriculum = "" }) {
  return SUBJECTS.map((s) => ({ ...s, ...subjectSummary(s.id, year, curriculum) }));
}

/** Next objective within a subject: review → practising → not_assessed (recommendation, never a gate). */
export function nextObjective(subject, year, curriculum = "") {
  const objs = objectivesFor(subject, year, curriculum);
  return objs.find((o) => o.state === "review") || objs.find((o) => o.state === "practising") || objs.find((o) => o.state === "not_assessed") || null;
}
